import { isEqual, omit } from 'lodash'
import PropTypes from 'prop-types'
import { useState, useEffect, useRef, useCallback } from 'react'
import Spinner from 'react-bootstrap/Spinner'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  ENVIO_A_CARGO_COMPRADOR,
  ENVIO_GRATIS,
  RETIRA_COMPRADOR
} from '../../../constants/mediosEnviosPagos.js'
import {
  ESTADO_PAUSADA,
  ESTADO_PUBLICADA,
  ESTADO_VENCIDA
} from '../../../constants/publicaciones.js'
import { toastErrorLog } from '../../../helpers/toastErrorLog'
import { useAuth } from '../../../hooks/useAuth'
import { setModal } from '../../../slices/modalSlice'
import { TitledBox } from '../../../ui/components/index.js'
import { formatearFecha } from '../../helpers/helper'
import { withValidador } from '../../hoc/withValidador'
import { useDomicilioPrincipalEBM } from '../../hooks/useDomicilioPrincipalEBM.js'
import Metadatos from '../../publicaciones/Metadatos'
import AgregarFotos from '../../publicar/AgregarFotos'
import DescripcionProducto from '../../publicar/DescripcionProducto'
import FechaPublicacion from '../../publicar/FechaPublicacion'
import IndicarCategorias from '../../publicar/IndicarCategorias'
import IndicarEtiquetas from '../../publicar/IndicarEtiquetas'
import ListaDeVentas from '../../publicar/ListaDeVentas'
import Ofertas from '../../publicar/Ofertas'
import VentasEspeciales from '../../publicar/VentasEspeciales'
import { chequearEanDuplicado, mensajeEanDuplicado } from '../../reutilizables/inputEAN/InputEAN'
import { GenericModal } from '../../reutilizables/modal/GenericModal'
import Precios from '../../reutilizables/precios/Precios.jsx'
import * as publicacionService from '../../servicios/publicacionesService'
import LinkButton from '../../ui/LinkButton'
import styles from './AdministrarPublicacion.module.css'
import CambioPrecio from './CambioPrecio'
import { getEsquema } from './esquema'
import Estado from './Estado'
import Exposicion from './Exposicion'

const FECHA = 10
const CAMPOS_OFERTA = ['porcDcto', 'ofertaDesde', 'ofertaHasta']
const TIMEOUT = 200
const ERROR_EAN_DUPLICADO = 409
const PASO = {
  PRINCIPIO: -1,
  VIGENCIA: 0,
  CAMBIO_PRECIO: 1,
  OFERTAS: 2,
  DESCRIPCION: 3,
  VENTAS_ESPECIALES: 4,
  ESTADO: 5,
  METADATOS: 6
}
const CAMPOS_ENVIO = [
  'descZona',
  'cantDiasEnvioGratis',
  'cantDiasRetiroComprador',
  'nombreListaEnvio',
  'mediosEnvios'
]
const AdministrarPublicacion = (props) => {
  const navigate = useNavigate()
  const [errores, setErrores] = useState({})
  const [grabando, setGrabando] = useState(false)
  const [publicacion, setPublicacion] = useState(props.publicacion)
  const [paso, setPaso] = useState(PASO.PRINCIPIO)
  const [hayCambios, setHayCambios] = useState(false)
  const [hayCambiosExternos, setHayCambiosExternos] = useState(false)
  const [accionEstado, setAccionEstado] = useState('PAUSAR_Y_REPUBLICAR')
  const [gtinPendiente, setGtinPendiente] = useState(null)
  const [errorGtin, setErrorGtin] = useState('')
  const [periodosProduccion, setPeriodosProduccion] = useState([])

  const dispatch = useDispatch()
  const vigenciaManualRef = useRef({
    desde: props.publicacion?.desde || '',
    hasta: props.publicacion?.hasta || ''
  })

  const pasoRefs = useRef({})
  const pasoRefSetters = useRef({})
  const ultimaActualizacion = useRef(publicacion)

  const getPasoElemento = useCallback((paso) => pasoRefs.current[paso] || null, [])

  const setPasoRef = useCallback((paso) => {
    if (!pasoRefSetters.current[paso]) {
      pasoRefSetters.current[paso] = (p) => {
        pasoRefs.current[paso] = p
      }
    }
    return pasoRefSetters.current[paso]
  }, [])

  const buscarPeriodosProduccion = async () => {
    const { data } = await publicacionService.buscarPeriodoTiempoProduccion()
    setPeriodosProduccion(data.body)
  }

  useEffect(() => {
    if (props.publicacion.aPedido) {
      buscarPeriodosProduccion()
    }
  }, [props.publicacion])

  const { loginDataContext: { idUsuario: idVendedor } = {} } = useAuth()
  const publicacionInicialRef = useRef(props.publicacion)
  const ultimaPublicacionInicializadaRef = useRef(null)
  const { domicilioEBMEstaCompleto, domicilioEBMResuelto, errorDomicilioEBM } =
    useDomicilioPrincipalEBM(idVendedor)

  const sanearRetiroComprador = useCallback(
    (pub) => {
      const tieneRetiro = pub.mediosEnvios?.some((m) => m.idMedioEnvio === RETIRA_COMPRADOR)
      if (!tieneRetiro || domicilioEBMEstaCompleto || errorDomicilioEBM) {
        return pub
      }
      return {
        ...pub,
        mediosEnvios: pub.mediosEnvios.filter((m) => m.idMedioEnvio !== RETIRA_COMPRADOR),
        cantDiasRetiroComprador: null
      }
    },
    [domicilioEBMEstaCompleto, errorDomicilioEBM]
  )

  useEffect(() => {
    ultimaActualizacion.current = publicacion
  }, [publicacion])

  useEffect(() => {
    const idPublicacionActual = Number(props.publicacion?.idPublicacion || 0)
    if (!idPublicacionActual) {
      return
    }

    const esNuevaPublicacion = ultimaPublicacionInicializadaRef.current !== idPublicacionActual
    if (!esNuevaPublicacion) {
      return
    }

    if (!domicilioEBMResuelto) {
      return
    }

    ultimaPublicacionInicializadaRef.current = idPublicacionActual

    if (Object.keys(props.publicacion).length > 0) {
      const publicacionSaneada = sanearRetiroComprador(props.publicacion)
      setPublicacion(publicacionSaneada)
      publicacionInicialRef.current = publicacionSaneada
      setHayCambios(false)
      const refInicio = getPasoElemento(PASO.PRINCIPIO)

      if (refInicio) {
        refInicio.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        })
      }
    }

    if (!Number(props.publicacion.idListaVenta)) {
      vigenciaManualRef.current = {
        desde: props.publicacion.desde || '',
        hasta: props.publicacion.hasta || ''
      }
    }
  }, [
    props.publicacion?.idPublicacion,
    getPasoElemento,
    sanearRetiroComprador,
    domicilioEBMResuelto
  ])

  const handleListaVentaChange = (listaSeleccionada) => {
    setPublicacion((prevState) => {
      if (listaSeleccionada && Number(listaSeleccionada.value) > 0) {
        if (!Number(prevState.idListaVenta)) {
          vigenciaManualRef.current = {
            desde: prevState.desde,
            hasta: prevState.hasta
          }
        }

        return {
          ...prevState,
          desde: listaSeleccionada.vigenciaDesde || prevState.desde,
          hasta: listaSeleccionada.vigenciaHasta || prevState.hasta
        }
      }

      return {
        ...prevState,
        desde: vigenciaManualRef.current.desde || prevState.desde,
        hasta: vigenciaManualRef.current.hasta || prevState.hasta
      }
    })
  }

  const pasoValido = useCallback(
    (name, value) => {
      const datosActualizados = {
        ...ultimaActualizacion.current,
        [name]: value
      }
      const datosParaValidarBase =
        value === undefined ? ultimaActualizacion.current : datosActualizados
      const datosParaValidar = {
        ...datosParaValidarBase,
        tieneRetiro: datosParaValidarBase.mediosEnvios?.some(
          (m) => m.idMedioEnvio === RETIRA_COMPRADOR
        ),
        tieneEnvioGratis: datosParaValidarBase.mediosEnvios?.some(
          (m) => m.idMedioEnvio === ENVIO_GRATIS
        )
      }

      const erroresValidacion = props.Validador.validarDato(getEsquema(), datosParaValidar, name)

      const requiereListaEnvio = datosParaValidar.mediosEnvios?.some(
        (medio) => medio.idMedioEnvio === ENVIO_A_CARGO_COMPRADOR
      )
      const errorListaEnvio =
        requiereListaEnvio && !datosParaValidar.idListaEnvio && !datosParaValidar.nombreListaEnvio
          ? 'Debes seleccionar una lista de envío para Envío a cargo del comprador.'
          : null

      setErrores((prev) => {
        const nuevos = { ...prev }

        if (!erroresValidacion || !erroresValidacion[name]) {
          delete nuevos[name]
        }

        if (!errorListaEnvio) {
          delete nuevos.idListaEnvio
        }

        return {
          ...nuevos,
          ...(erroresValidacion || {}),
          ...(errorListaEnvio ? { idListaEnvio: errorListaEnvio } : {})
        }
      })

      const hayErrores = Boolean(erroresValidacion) || Boolean(errorListaEnvio)
      return !hayErrores
    },
    [props.Validador]
  )

  const handleChangeState = (name, value, callBack) => {
    ultimaActualizacion.current = { ...ultimaActualizacion.current, [name]: value }
    setPublicacion((prevState) => ({
      ...prevState,
      [name]: value
    }))
    if (callBack) {
      callBack()
    }
  }

  const normalizarMedios = (medios, idField) =>
    (medios || []).map((m) => m[idField]).sort((a, b) => a - b)

  const normalizarPublicacion = (pub) => ({
    ...omit(pub, [
      'desde',
      'mediosPagos',
      'mediosEnvios',
      'garantias',
      'porcCambio',
      'importeCambio',
      'idListaEnvio'
    ]),
    cantDisponible: Number(pub.cantDisponible) || 0,
    alicuota: Number(pub.alicuota) || 0,
    idListaVenta: pub.idListaVenta ? Number(pub.idListaVenta) : null,
    nombreListaEnvio: pub.nombreListaEnvio || null,
    descZona: pub.descZona || null,
    cantDiasEnvioGratis: pub.cantDiasEnvioGratis ?? null,
    cantDiasRetiroComprador: pub.cantDiasRetiroComprador ?? null,
    mediosPagos: normalizarMedios(pub.mediosPagos, 'idMedioPago'),
    mediosEnvios: normalizarMedios(pub.mediosEnvios, 'idMedioEnvio'),
    garantias: normalizarMedios(pub.garantias, 'idGarantia')
  })

  useEffect(() => {
    const sonIguales = isEqual(
      normalizarPublicacion(publicacion),
      normalizarPublicacion(publicacionInicialRef.current)
    )
    const valorFinal = !sonIguales || hayCambiosExternos || gtinPendiente !== null
    setHayCambios(valorFinal)
  }, [publicacion, hayCambiosExternos, gtinPendiente])

  const obtenerValor = useCallback((value, valueCalculado, checked, type, tipoDato) => {
    let val
    switch (tipoDato) {
      case 'bool':
        val = value === 'true'
        break
      case 'num':
        val = Number(value)
        break
      case 'calc':
        val = valueCalculado
        break
      default:
        val = type === 'checkbox' || type === 'radio' ? checked : value
        break
    }
    return val
  }, [])

  const abrirModal = useCallback(
    (estadoNuevo, accionOverride) => {
      const accionEfectiva = accionOverride ?? accionEstado
      const nombreEstado =
        estadoNuevo === ESTADO_PAUSADA
          ? 'pausada'
          : estadoNuevo === ESTADO_VENCIDA
            ? 'vencida'
            : 'publicada'
      const TEXTOS = {
        ['UPDATE_SIMPLE']: {
          title: 'Cambio de estado',
          body: `Estás a punto de cambiar el estado de la publicación "${publicacion.titulo}" a ${nombreEstado}. 

          ${
            nombreEstado === 'vencida'
              ? 'La publicación cambiará de estado y la "Fecha de vigencia hasta" se actualizará automáticamente con la fecha actual.'
              : 'La publicación cambiará de estado y dejará de estar disponible para otros compradores.'
          }

          El resto de los datos de la publicación no se modificarán.

          ¿Estás seguro de que deseas continuar?`
        },
        ['PAUSAR_Y_REPUBLICAR']: {
          title: 'Aviso de pausa y publicación automática',
          body: `Como se han generado cambios, al guardar la publicación se pausará y se publicará automáticamente pasados los 30 minutos de la modificación. 
          ¿Deseas continuar?`
        }
      }
      const { title, body } = TEXTOS[accionEfectiva] ?? TEXTOS['UPDATE_SIMPLE']
      dispatch(
        setModal({ size: 'sm', title, type: 'botones', body, show: true, modalId: 'cambio-estado' })
      )
    },
    [dispatch, publicacion, accionEstado]
  )

  const manejarCambioEstado = (esValido) => {
    pasoValido('idPublicacionEstado', ultimaActualizacion.current.idPublicacionEstado)

    const estabaPausada = publicacionInicialRef.current.idPublicacionEstado === ESTADO_PAUSADA

    const quierePublicar = ultimaActualizacion.current.idPublicacionEstado === ESTADO_PUBLICADA

    if (estabaPausada && quierePublicar && esValido && !hayCambios) {
      handleChangeState('idPublicacionEstado', publicacionInicialRef.current.idPublicacionEstado)
    }
  }

  const handleChange = useCallback(
    ({ currentTarget: input } = {}, tipoDato = '', valueCalculado = null) => {
      if (!input) {
        return
      }
      const { name, value, checked, type } = input
      const valorEfectivo = obtenerValor(value, valueCalculado, checked, type, tipoDato)
      handleChangeState(name, valorEfectivo, () => {
        const esValido = pasoValido(name, valorEfectivo)
        if (name === 'cantMinPedido') {
          pasoValido('capacidadMax', ultimaActualizacion.current.capacidadMax)
        }
        if (name === 'capacidadMax') {
          pasoValido('cantMinPedido', ultimaActualizacion.current.cantMinPedido)
        }
        if (name === 'hasta') {
          manejarCambioEstado(esValido)
        }
      })
      if (name === 'idPublicacionEstado' && valorEfectivo !== ESTADO_PUBLICADA) {
        setAccionEstado('UPDATE_SIMPLE')
        abrirModal(valorEfectivo, 'UPDATE_SIMPLE')
      }
    },
    [pasoValido, handleChangeState, obtenerValor, hayCambios, hayCambiosExternos, abrirModal]
  )

  useEffect(() => {
    const refPaso = getPasoElemento(paso)
    if (refPaso) {
      refPaso.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    }
  }, [paso, getPasoElemento])

  const setErrorFechaPublicacion = () => {
    setErrores((prev) => ({
      ...prev,
      hasta: (
        <>
          Ups, la fecha de vigencia es inválida. Como la publicación se va a publicar
          automáticamente, la {'"Fecha Hasta"'} debe ser mayor o igual a la fecha actual.
        </>
      )
    }))
  }

  const obtenerCamposModificados = useCallback(() => {
    const inicial = normalizarPublicacion(publicacionInicialRef.current)
    const actual = normalizarPublicacion(publicacion)

    return Object.keys(actual).reduce(
      (acc, key) => {
        if (!isEqual(actual[key], inicial[key])) {
          acc[key] = actual[key]
        }
        return acc
      },
      { idPublicacion: actual.idPublicacion }
    )
  }, [publicacion])

  const cambiaronCampos = (camposModificados, campos) =>
    campos.some((campo) => campo in camposModificados)

  const obtenerPayloadUpdateSimple = () => ({
    idPublicacion: publicacion.idPublicacion,
    idPublicacionEstado: publicacion.idPublicacionEstado,
    idVendedor,
    hasta:
      publicacion.idPublicacionEstado === ESTADO_PAUSADA
        ? formatearFecha(publicacion.hasta, 'ISO')
        : undefined
  })

  const obtenerPayloadPausarYRepublicar = (camposModificados) => {
    const payload = {
      ...omit(camposModificados, [
        ...CAMPOS_OFERTA,
        'mediosPagos',
        'mediosEnvios',
        'garantias',
        'descZona',
        'cantDiasEnvioGratis',
        'cantDiasRetiroComprador',
        'nombreListaEnvio'
      ]),
      idPublicacion: publicacion.idPublicacion,
      idPublicacionEstado: publicacion.idPublicacionEstado,
      idVendedor
    }

    if ('mediosPagos' in camposModificados) {
      payload.mediosPagos = publicacion.mediosPagos
    }

    if ('garantias' in camposModificados) {
      payload.garantias = publicacion.garantias
    }

    if ('etiquetas' in camposModificados) {
      payload.etiquetas = publicacion.etiquetas ?? ''
    }

    if (cambiaronCampos(camposModificados, CAMPOS_ENVIO)) {
      payload.mediosEnvios = publicacion.mediosEnvios
      payload.descZona = publicacion.descZona ?? null
      payload.cantDiasEnvioGratis = publicacion.cantDiasEnvioGratis ?? null
      payload.cantDiasRetiroComprador = publicacion.cantDiasRetiroComprador ?? null
      payload.nombreListaEnvio = publicacion.nombreListaEnvio ?? null
    }

    return payload
  }

  const obtenerPublicacionActualizada = useCallback(() => {
    const camposModificados = obtenerCamposModificados()

    if (accionEstado === 'UPDATE_SIMPLE') {
      return obtenerPayloadUpdateSimple()
    }
    return obtenerPayloadPausarYRepublicar(camposModificados)
  }, [accionEstado, publicacion, obtenerCamposModificados])

  const confirmarCambioPublicacion = async () => {
    if (accionEstado === 'PAUSAR_Y_REPUBLICAR' && !pasoValido('hasta', publicacion.hasta)) {
      dispatch(setModal({ show: false }))
      setErrorFechaPublicacion()
      setPaso(PASO.VIGENCIA)
      setTimeout(() => {
        const ref = getPasoElemento(PASO.VIGENCIA)
        if (ref) {
          ref.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, TIMEOUT)
      return false
    }

    if (accionEstado === 'PAUSAR_Y_REPUBLICAR') {
      try {
        const { data: metadatos } = await publicacionService.buscarMetadato(
          publicacion.idPublicacion
        )
        const gtin = metadatos?.find((m) => m.idMetaDato === 'GTIN')?.valor ?? ''
        const duplicada = await chequearEanDuplicado(
          gtin,
          publicacion.idVendedor,
          publicacion.idPublicacion
        )
        if (duplicada) {
          dispatch(setModal({ show: false }))
          setErrores((prev) => ({
            ...prev,
            idPublicacionEstado: mensajeEanDuplicado(duplicada, { conId: true })
          }))
          setPaso(PASO.ESTADO)
          return false
        }
      } catch {
        dispatch(setModal({ show: false }))
        toastErrorLog(null, 'Ocurrió un error al verificar el EAN.', 'publicaciones')
        return false
      }
    }

    setGrabando(true)
    return guardarPublicacion(obtenerPublicacionActualizada())
  }

  const cancelarCambioPublicacion = () => {
    handleChangeState('idPublicacionEstado', publicacionInicialRef.current.idPublicacionEstado)
    setAccionEstado('PAUSAR_Y_REPUBLICAR')
    setErrores((prev) => ({
      ...prev,
      idPublicacionEstado: null
    }))
  }

  const guardarPublicacion = async (publicacionActualizada) => {
    try {
      await publicacionService.actualizarPublicacion(publicacionActualizada)
      setGrabando(false)
      cancelarForm()
      return true
    } catch (error) {
      setGrabando(false)
      const duplicada = error?.response?.status === ERROR_EAN_DUPLICADO ? error.response.data : null
      if (duplicada?.error === 'EAN_DUPLICADO') {
        handleChangeState('idPublicacionEstado', publicacionInicialRef.current.idPublicacionEstado)
        handleChangeState('fechaReactivacion', null)
        setErrores((prev) => ({
          ...prev,
          idPublicacionEstado: mensajeEanDuplicado(duplicada, { conId: true })
        }))
        setPaso(PASO.ESTADO)
        return false
      }
      toastErrorLog(error, 'Ocurrió un error al actualizar la publicación.', 'publicaciones')
      return false
    }
  }

  const validarForm = useCallback(async () => {
    const validacion = [
      { error: 'descripcion', paso: PASO.DESCRIPCION },
      { error: 'hasta', paso: PASO.VIGENCIA },
      { error: 'precioUnitario', paso: PASO.CAMBIO_PRECIO },
      { error: 'alicuota', paso: PASO.CAMBIO_PRECIO },
      { error: 'cantDisponible', paso: PASO.CAMBIO_PRECIO },
      { error: 'cantMinPedido', paso: PASO.CAMBIO_PRECIO },
      { error: 'capacidadMax', paso: PASO.CAMBIO_PRECIO },
      { error: 'cantTiempoProd', paso: PASO.CAMBIO_PRECIO },
      { error: 'periodoTiempoProd', paso: PASO.CAMBIO_PRECIO },
      { error: 'preciosMayorista', paso: PASO.CAMBIO_PRECIO },
      { error: 'mediosPagos', paso: PASO.VENTAS_ESPECIALES },
      { error: 'mediosEnvios', paso: PASO.VENTAS_ESPECIALES },
      { error: 'cantDiasEnvioGratis', paso: PASO.VENTAS_ESPECIALES },
      { error: 'cantDiasRetiroComprador', paso: PASO.VENTAS_ESPECIALES },
      { error: 'descZona', paso: PASO.VENTAS_ESPECIALES },
      { error: 'idPublicacionEstado', paso: PASO.ESTADO }
    ]

    const estadoNuevo = publicacion.idPublicacionEstado

    const vaAPublicar = accionEstado === 'PAUSAR_Y_REPUBLICAR'

    const hayError = validacion.find((campo) => {
      return !pasoValido(campo.error, publicacion[campo.error])
    })

    if (hayError) {
      if (hayError.error === 'hasta' && vaAPublicar) {
        setErrorFechaPublicacion()
      }
      setPaso(hayError.paso)
      return
    }

    if (gtinPendiente !== null) {
      try {
        const duplicada = await chequearEanDuplicado(
          gtinPendiente,
          publicacion.idVendedor,
          publicacion.idPublicacion
        )
        if (duplicada) {
          setErrorGtin(mensajeEanDuplicado(duplicada, { conId: true }))
          setPaso(PASO.METADATOS)
          return
        }
      } catch {
        toastErrorLog(null, 'Ocurrió un error al verificar el EAN.', 'publicaciones')
        return
      }

      try {
        await publicacionService.InsertarMetadato({
          idPublicacion: publicacion.idPublicacion,
          idMetaDato: 'GTIN',
          valor: gtinPendiente || null
        })
        setGtinPendiente(null)
      } catch (error) {
        toastErrorLog(error, 'Error al insertar la característica', 'caracteristica')
        return
      }
    }

    abrirModal(estadoNuevo)
  }, [
    pasoValido,
    publicacion,
    obtenerPublicacionActualizada,
    guardarPublicacion,
    hayCambios,
    hayCambiosExternos,
    abrirModal,
    gtinPendiente
  ])

  const claseActivo = (posicion) => paso === posicion

  const cancelarForm = () => {
    navigate('/Publicaciones/Administrar')
  }

  const aplicarDescuento = async (descuento, desde, hasta) => {
    const camposOferta = [
      { error: 'porcDcto', value: descuento },
      { error: 'ofertaDesde', value: desde },
      { error: 'ofertaHasta', value: hasta }
    ]
    const hayErrorOferta = camposOferta.some(({ error, value }) => !pasoValido(error, value))
    if (hayErrorOferta) {
      setPaso(PASO.OFERTAS)
      return
    }
    setGrabando(true)
    try {
      await publicacionService.ActualizarPublicacionIndividual({
        IdPublicacion: publicacion.idPublicacion,
        PrecioUnitario: publicacion.precioUnitario,
        IdMoneda: publicacion.idMoneda,
        OfertaDesde: desde,
        OfertaHasta: hasta,
        porcDcto: descuento
      })
      toast.success('Descuento aplicado correctamente')
    } catch (error) {
      const mensaje = `Error al aplicar descuento.`
      toastErrorLog(error, mensaje, 'publicaciones')
    }
    setGrabando(false)
  }

  const borrarDescuento = async () => {
    setGrabando(true)
    try {
      await publicacionService.ActualizarPublicacionIndividual({
        IdPublicacion: publicacion.idPublicacion,
        PrecioUnitario: publicacion.precioUnitario,
        IdMoneda: publicacion.idMoneda,
        OfertaDesde: null,
        OfertaHasta: null,
        porcDcto: null
      })
      toast.success('Descuento eliminado correctamente')
    } catch (error) {
      const mensaje = `Error al eliminar descuento.`
      toastErrorLog(error, mensaje, 'publicaciones')
    }
    setGrabando(false)
  }

  const fechaHastaFormateada = formatearFecha(publicacion.hasta, 'ISO')

  return (
    <>
      <form className={styles.contenedorGeneral} onSubmit={(e) => e.preventDefault()}>
        <div className={styles.headerDetalle} ref={setPasoRef(PASO.PRINCIPIO)}>
          <span className={styles.nombrePublicacion}>{publicacion.titulo}</span>
          <div className={styles.columnaAuto}>
            <div className={styles.bold}>Items vendidos: {publicacion.totalItemsPedidos}</div>
          </div>
        </div>
        <div className={styles.flexColumn}>
          <div className={styles.flexRow}>
            <TitledBox titulo="Exposición">
              <Exposicion
                onChangeState={handleChangeState}
                idExposicionAnterior={props.publicacion.idPublicacionExposicion}
                idExposicionNueva={publicacion.idPublicacionExposicion}
              />
            </TitledBox>
            <TitledBox
              titulo="Vigencia"
              error={errores.hasta}
              ref={setPasoRef(PASO.VIGENCIA)}
              activo={claseActivo(PASO.VIGENCIA)}
            >
              <FechaPublicacion
                vieneDeAdministrar={true}
                onChange={handleChange}
                publicacion={publicacion}
              />
            </TitledBox>
            <TitledBox titulo="Categoría">
              <IndicarCategorias
                onChange={handleChange}
                value={publicacion.idCategoria}
                esScrap={publicacion.esScrap}
              />
            </TitledBox>
            <TitledBox titulo="Incluir en Lista de Ventas">
              <ListaDeVentas
                vieneDeAdministrar={true}
                idVendedor={idVendedor}
                onChange={handleChange}
                onChangeState={handleChangeState}
                onListaVentaChange={handleListaVentaChange}
                value={publicacion.idListaVenta ? publicacion.idListaVenta : 0}
                publicacion={publicacion}
              />
            </TitledBox>
          </div>
          <div className={styles.flexColumn}>
            <div className={styles.flexRow}>
              <TitledBox titulo="Etiquetas">
                <IndicarEtiquetas
                  vieneDeAdministrar={true}
                  onChangeState={handleChangeState}
                  value={publicacion.etiquetas}
                />
              </TitledBox>
              <TitledBox
                titulo="Estado Publicación"
                error={errores.idPublicacionEstado}
                ref={setPasoRef(PASO.ESTADO)}
                activo={claseActivo(PASO.ESTADO)}
              >
                <Estado
                  onChange={handleChange}
                  value={publicacion.idPublicacionEstado}
                  vigenciaHasta={fechaHastaFormateada?.slice(0, FECHA)}
                  fechaReactivacion={publicacion.fechaReactivacion}
                  estadoInicial={publicacionInicialRef.current.idPublicacionEstado}
                />
              </TitledBox>
            </div>
          </div>

          {publicacion.precioUnitario !== 0 && !publicacion.conPresupuesto && (
            <div className={styles.flexRow}>
              <TitledBox
                titulo={
                  <div className={styles.tituloSpaceBetween}>
                    <span>Cambio de Precio</span>
                    <div className={styles.precioDisplay}>
                      Total con IVA:
                      {
                        <Precios
                          precio={publicacion.precioUnitario}
                          alicuota={publicacion.alicuota}
                          classNameValor={styles.precioValorConIVA}
                          monedaSimbolo={publicacion.monedaSimbolo}
                        />
                      }
                    </div>
                  </div>
                }
                error={
                  errores.cantDisponible ||
                  errores.cantMinPedido ||
                  errores.capacidadMax ||
                  errores.cantTiempoProd ||
                  errores.periodoTiempoProd ||
                  errores.precioUnitario ||
                  errores.alicuota
                }
                ref={setPasoRef(PASO.CAMBIO_PRECIO)}
                activo={claseActivo(PASO.CAMBIO_PRECIO)}
              >
                <CambioPrecio
                  idPublicacion={publicacion.idPublicacion}
                  onChangeState={handleChangeState}
                  value={publicacion.precioUnitario}
                  alicuota={publicacion.alicuota}
                  precioMayorista={publicacion.preciosMayorista}
                  isDisabled={false}
                  cantDisponible={publicacion.cantDisponible}
                  onChange={handleChange}
                  aPedido={publicacion.aPedido}
                  cantMinPedido={publicacion.cantMinPedido}
                  capacidadMax={publicacion.capacidadMax}
                  cantTiempoProd={publicacion.cantTiempoProd}
                  periodoTiempoProd={publicacion.periodoTiempoProd}
                  periodosProduccion={periodosProduccion}
                />
              </TitledBox>
              <div className={styles.flexColumn}>
                <TitledBox
                  titulo="Oferta"
                  error={errores.porcDcto || errores.ofertaDesde || errores.ofertaHasta}
                  ref={setPasoRef(PASO.OFERTAS)}
                  activo={claseActivo(PASO.OFERTAS)}
                >
                  <Ofertas
                    vieneDeAdministrar={true}
                    values={{
                      porcDcto: publicacion.porcDcto ?? props.publicacion.porcDcto ?? 0,
                      porcDctoPublicacion:
                        publicacion.porcDctoPublicacion ??
                        props.publicacion.porcDctoPublicacion ??
                        0,
                      porcDctoLista:
                        publicacion.porcDctoLista ?? props.publicacion.porcDctoLista ?? 0,
                      precioUnitario:
                        publicacion.precioUnitario ?? props.publicacion.precioUnitario ?? 0,
                      alicuota: publicacion.alicuota ?? props.publicacion.alicuota ?? 0,
                      ofertaDesde: publicacion.ofertaDesde ?? props.publicacion.ofertaDesde ?? '',
                      ofertaHasta: publicacion.ofertaHasta ?? props.publicacion.ofertaHasta ?? '',
                      vigenciaDesde: publicacion.desde ?? props.publicacion.desde ?? '',
                      vigenciaHasta: publicacion.hasta ?? props.publicacion.hasta ?? '',
                      listaVigenciaDesde:
                        publicacion.listaVigenciaDesde ??
                        props.publicacion.listaVigenciaDesde ??
                        '',
                      listaVigenciaHasta:
                        publicacion.listaVigenciaHasta ??
                        props.publicacion.listaVigenciaHasta ??
                        '',
                      listaOfertaDesde:
                        publicacion.listaOfertaDesde ?? props.publicacion.listaOfertaDesde ?? '',
                      listaOfertaHasta:
                        publicacion.listaOfertaHasta ?? props.publicacion.listaOfertaHasta ?? ''
                    }}
                    onChange={handleChange}
                  />
                  <div className="mt-2 d-flex gap-2">
                    <button
                      type="button"
                      className="btn btn-success mr-2"
                      onClick={() =>
                        aplicarDescuento(
                          publicacion.porcDcto,
                          publicacion.ofertaDesde,
                          publicacion.ofertaHasta
                        )
                      }
                      disabled={
                        !publicacion.porcDcto ||
                        !publicacion.ofertaDesde ||
                        !publicacion.ofertaHasta
                      }
                    >
                      Aplicar descuento
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={borrarDescuento}
                      disabled={
                        publicacion.porcDctoPublicacion === 0 ||
                        publicacion.porcDctoPublicacion === null
                      }
                    >
                      Eliminar descuento
                    </button>
                  </div>
                </TitledBox>
              </div>
            </div>
          )}
          <div className={styles.flexRow}>
            <TitledBox
              titulo="Características del producto"
              error={errorGtin}
              ref={setPasoRef(PASO.METADATOS)}
              activo={claseActivo(PASO.METADATOS)}
            >
              <Metadatos
                idPublicacion={publicacion.idPublicacion}
                idCategoria={publicacion.idCategoria}
                editable={true}
                onChange={handleChange}
                onChangeExterno={(cambio) => setHayCambiosExternos(cambio)}
                onGtinPendiente={(valor) => {
                  setGtinPendiente(valor)
                  if (errorGtin) setErrorGtin('')
                }}
                errorGtinExterno={errorGtin}
              />
            </TitledBox>
          </div>
          <div className={`${styles.flexColumn} ${styles.w100}`}>
            <TitledBox
              titulo="Escribí la descripción de tu producto"
              error={errores.descripcion}
              ref={setPasoRef(PASO.DESCRIPCION)}
              activo={claseActivo(PASO.DESCRIPCION)}
            >
              <DescripcionProducto onChange={handleChange} value={publicacion.descripcion} />
            </TitledBox>
            <TitledBox titulo="Fotografías">
              <AgregarFotos
                value={publicacion.youtube || ''}
                vieneDeAdministrar={true}
                onChange={handleChange}
                idPublicacion={publicacion.idPublicacion}
                idPublicacionTemporal={0}
              />
            </TitledBox>
          </div>
          <TitledBox
            titulo="Método de Pago y Envío"
            ref={setPasoRef(PASO.VENTAS_ESPECIALES)}
            activo={claseActivo(PASO.VENTAS_ESPECIALES)}
            error={
              errores.mediosPagos ||
              errores.mediosEnvios ||
              errores.idListaEnvio ||
              errores.cantDiasEnvioGratis ||
              errores.cantDiasRetiroComprador ||
              errores.descZona
            }
          >
            <VentasEspeciales
              vieneDeAdministrar={true}
              idPublicacion={publicacion.idPublicacion}
              values={{
                extensionGarantia: publicacion.extensionGarantia,
                garantias: publicacion.garantias,
                mediosPagos: publicacion.mediosPagos,
                mediosEnvios: publicacion.mediosEnvios,
                envioGratis: publicacion.envioGratis,
                descZona: publicacion.descZona,
                cantDiasEnvioGratis: publicacion.cantDiasEnvioGratis,
                cantDiasRetiroComprador: publicacion.cantDiasRetiroComprador,
                idListaEnvio: publicacion.idListaEnvio,
                nombreListaEnvio: publicacion.nombreListaEnvio
              }}
              onChange={handleChange}
            />
          </TitledBox>
        </div>

        <div className={styles.botones}>
          <LinkButton className={styles.btnPrincipal} onClick={validarForm} disabled={!hayCambios}>
            {grabando ? (
              <span>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="mr-2"
                />
                Guardando...
              </span>
            ) : (
              <span>
                <i className="fa fa-check mr-2" aria-hidden="true" />
                Aceptar
              </span>
            )}
          </LinkButton>
          <LinkButton className={styles.btnSecundario} onClick={cancelarForm}>
            <i className="fa fa-times mr-2" aria-hidden="true" />
            Cancelar
          </LinkButton>
        </div>
        <GenericModal
          modalId="cambio-estado"
          onConfirm={confirmarCambioPublicacion}
          onClose={cancelarCambioPublicacion}
        />
      </form>
    </>
  )
}

AdministrarPublicacion.propTypes = {
  publicacion: PropTypes.shape({
    idPublicacionEstado: PropTypes.number.isRequired,
    cantDisponible: PropTypes.number.isRequired,
    idPublicacionExposicion: PropTypes.number.isRequired,
    idPublicacion: PropTypes.number.isRequired,
    idVendedor: PropTypes.number.isRequired,
    titulo: PropTypes.string.isRequired,
    monedaSimbolo: PropTypes.string.isRequired,
    precioUnitario: PropTypes.number.isRequired,
    totalItemsPedidos: PropTypes.number.isRequired,
    publicacionEstado: PropTypes.string.isRequired,
    publicacionExposicion: PropTypes.string.isRequired,
    desde: PropTypes.string,
    hasta: PropTypes.string,
    etiquetas: PropTypes.string,
    idListaVenta: PropTypes.number,
    idCategoria: PropTypes.number,
    esScrap: PropTypes.bool,
    descripcion: PropTypes.string,
    youtube: PropTypes.string,
    extensionGarantia: PropTypes.number,
    garantias: PropTypes.array,
    mediosPagos: PropTypes.array,
    mediosEnvios: PropTypes.array,
    envioGratis: PropTypes.bool,
    porcDcto: PropTypes.number,
    porcDctoPublicacion: PropTypes.number,
    porcDctoLista: PropTypes.number,
    listaVigenciaDesde: PropTypes.string,
    listaVigenciaHasta: PropTypes.string,
    listaOfertaDesde: PropTypes.string,
    listaOfertaHasta: PropTypes.string,
    ofertaDesde: PropTypes.string,
    ofertaHasta: PropTypes.string,
    alicuota: PropTypes.number,
    aPedido: PropTypes.bool,
    cantTiempoProd: PropTypes.number,
    periodoTiempoProd: PropTypes.number,
    periodosProduccion: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        periodo: PropTypes.string.isRequired
      })
    )
  }).isRequired,
  idPublicacion: PropTypes.number.isRequired,
  setPublicacion: PropTypes.func.isRequired,
  Validador: PropTypes.shape({
    validarDato: PropTypes.func.isRequired
  }).isRequired
}

export default withValidador(AdministrarPublicacion)
